import { g17ecoApi, transformResponse } from './g17ecoApi';
import type {
  InitiativeGroup,
  CreateInitiativeGroup,
  UpdateInitiativeGroup,
} from '../types/initiative-group';

export enum Tag {
  SubsidiaryGroups = 'SubsidiaryGroups',
  SubsidiaryGroup = 'SubsidiaryGroup',
}

const getBaseUrl = (initiativeId: string) => `/initiatives/${initiativeId}/subsidiary-groups`;

export const subsidiaryGroupsApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: Object.values(Tag),
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getSubsidiaryGroups: builder.query<InitiativeGroup[], string>({
        transformResponse,
        query: (initiativeId) => ({
          url: getBaseUrl(initiativeId),
          method: 'get',
        }),
        providesTags: (_result, _error, initiativeId) => [{ type: Tag.SubsidiaryGroups, id: initiativeId }],
      }),

      getSubsidiaryGroup: builder.query<InitiativeGroup, { initiativeId: string; groupId: string }>({
        transformResponse,
        query: ({ initiativeId, groupId }) => ({
          url: `${getBaseUrl(initiativeId)}/${groupId}`,
          method: 'get',
        }),
        providesTags: (_result, _error, { groupId }) => [{ type: Tag.SubsidiaryGroup, id: groupId }],
      }),

      createSubsidiaryGroup: builder.mutation<
        InitiativeGroup,
        { initiativeId: string; data: CreateInitiativeGroup }
      >({
        transformResponse,
        query: ({ initiativeId, data }) => ({
          url: getBaseUrl(initiativeId),
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, { initiativeId }) => [{ type: Tag.SubsidiaryGroups, id: initiativeId }],
      }),

      updateSubsidiaryGroup: builder.mutation<
        InitiativeGroup,
        { initiativeId: string; groupId: string; data: UpdateInitiativeGroup }
      >({
        transformResponse,
        query: ({ initiativeId, groupId, data }) => ({
          url: `${getBaseUrl(initiativeId)}/${groupId}`,
          method: 'put',
          data,
        }),
        invalidatesTags: (_result, _error, { initiativeId, groupId }) => [
          { type: Tag.SubsidiaryGroups, id: initiativeId },
          { type: Tag.SubsidiaryGroup, id: groupId },
        ],
      }),

      deleteSubsidiaryGroup: builder.mutation<
        void,
        { initiativeId: string; groupId: string }
      >({
        query: ({ initiativeId, groupId }) => ({
          url: `${getBaseUrl(initiativeId)}/${groupId}`,
          method: 'delete',
        }),
        invalidatesTags: (_result, _error, { initiativeId }) => [{ type: Tag.SubsidiaryGroups, id: initiativeId }],
      }),

      duplicateSubsidiaryGroup: builder.mutation<
        InitiativeGroup,
        { initiativeId: string; groupId: string; newName: string }
      >({
        transformResponse,
        query: ({ initiativeId, groupId, newName }) => ({
          url: `${getBaseUrl(initiativeId)}/${groupId}/duplicate`,
          method: 'post',
          data: { newName },
        }),
        invalidatesTags: (_result, _error, { initiativeId }) => [{ type: Tag.SubsidiaryGroups, id: initiativeId }],
      }),

      updateSubsidiaryGroups: builder.mutation<
        void,
        { rootInitiativeId: string; subsidiaryId: string; groupIdsToAdd: string[]; groupIdsToRemove: string[] }
      >({
        query: ({ rootInitiativeId, subsidiaryId, groupIdsToAdd, groupIdsToRemove }) => ({
          url: `${getBaseUrl(rootInitiativeId)}/subsidiary/${subsidiaryId}`,
          method: 'put',
          data: { groupIdsToAdd, groupIdsToRemove },
        }),
        invalidatesTags: (_result, _error, { rootInitiativeId }) => [{ type: Tag.SubsidiaryGroups, id: rootInitiativeId }],
      }),
    }),
  });

export const {
  useGetSubsidiaryGroupsQuery,
  useLazyGetSubsidiaryGroupsQuery,
  useGetSubsidiaryGroupQuery,
  useCreateSubsidiaryGroupMutation,
  useUpdateSubsidiaryGroupMutation,
  useDeleteSubsidiaryGroupMutation,
  useDuplicateSubsidiaryGroupMutation,
  useUpdateSubsidiaryGroupsMutation,
} = subsidiaryGroupsApi;
